import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { toast } from 'sonner';

export interface BlogPost {
  id: string | number;
  slug: string;
  title: string;
  excerpt: string;
  content?: string;
  date: string;
  imageUrl?: string;
  categories: string[];
  author?: string;
  readTime?: string;
  category?: string;
  tags?: string[];
  featured?: boolean;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
}

const SAVED_ARTICLES_KEY = 'kaufpause-saved-articles';

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post, featured = false }) => {
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  
  // Formatiere das Datum
  let formattedDate;
  try {
    formattedDate = format(new Date(post.date), 'dd. MMM yyyy', { locale: de });
  } catch (e) {
    formattedDate = post.date;
  }
  
  // Prüfe, ob der Artikel gespeichert ist
  useEffect(() => {
    const savedArticles = localStorage.getItem(SAVED_ARTICLES_KEY);
    if (savedArticles) {
      const articles = JSON.parse(savedArticles);
      setIsSaved(articles.some((article: {slug: string}) => article.slug === post.slug));
    }
  }, [post.slug]);

  // Speichern oder Entfernen des Artikels
  const toggleSave = (e: React.MouseEvent) => {
    e.stopPropagation(); // Verhindere Navigation beim Klicken
    
    try {
      const savedArticles = localStorage.getItem(SAVED_ARTICLES_KEY);
      let articles = savedArticles ? JSON.parse(savedArticles) : [];
      
      if (isSaved) {
        // Artikel aus der gespeicherten Liste entfernen
        articles = articles.filter((article: {slug: string}) => article.slug !== post.slug);
        toast.success('Artikel aus Lesezeichen entfernt');
      } else {
        // Artikel zur gespeicherten Liste hinzufügen
        const articleToSave = {
          id: post.id,
          slug: post.slug,
          title: post.title,
          excerpt: post.excerpt,
          date: post.date,
          imageUrl: post.imageUrl,
          categories: post.categories
        };
        articles.push(articleToSave);
        toast.success('Artikel zu Lesezeichen hinzugefügt');
      }
      
      localStorage.setItem(SAVED_ARTICLES_KEY, JSON.stringify(articles));
      setIsSaved(!isSaved);
    } catch (err) {
      console.error('Fehler beim Speichern des Artikels:', err);
      toast.error('Der Artikel konnte nicht gespeichert werden');
    }
  };

  return (
    <Card 
      className={`overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer group h-full flex flex-col border border-gray-100 rounded-2xl ${
        featured ? 'md:flex-row md:h-64' : ''
      }`}
      onClick={() => navigate(`/blog/${post.slug}`)}
    >
      {post.imageUrl && (
        <div className={`relative overflow-hidden ${featured ? 'md:w-2/5' : 'h-48'}`}>
          <img 
            src={post.imageUrl || 'https://images.unsplash.com/photo-1591696205602-2f950c417cb9?q=80&w=800&auto=format&fit=crop'}
            alt={post.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <button
            onClick={toggleSave}
            className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-all duration-200 shadow-sm hover:shadow-md"
            title={isSaved ? "Aus Lesezeichen entfernen" : "Zu Lesezeichen hinzufügen"}
          >
            {isSaved ? (
              <BookmarkCheck className="h-4 w-4 text-emerald-600" />
            ) : (
              <Bookmark className="h-4 w-4 text-gray-600" />
            )}
          </button>
        </div>
      )}
      
      <div className={`flex flex-col flex-grow p-6 ${featured ? 'md:w-3/5' : ''}`}>
        <div className="mb-3 flex flex-wrap gap-2">
          {post.categories.slice(0, 2).map((category, index) => (
            <Badge key={index} variant="outline" className="text-xs px-3 py-1 bg-emerald-50 text-emerald-700 border-emerald-200 rounded-xl">
              {category}
            </Badge>
          ))}
          {post.categories.length > 2 && (
            <Badge variant="outline" className="text-xs px-3 py-1 bg-gray-50 text-gray-600 border-gray-200 rounded-xl">
              +{post.categories.length - 2}
            </Badge>
          )}
        </div>
        
        <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-emerald-600 transition-colors duration-200 line-clamp-2">
          {post.title}
        </h3>
        
        <p className="text-sm text-gray-600 line-clamp-3 mb-4 flex-grow leading-relaxed">
          {post.excerpt}
        </p>
        
        <div className="text-xs text-gray-500 mt-auto font-medium">
          {formattedDate}
        </div>
      </div>
    </Card>
  );
};

export default BlogCard; 